import { useState, useCallback } from 'react';

export interface UseCurrencyInputReturn {
  displayValue: string;
  centValue: number;
  handleChange: (value: string) => void;
  setValue: (cents: number) => void;
}

/**
 * Hook for currency input with dynamic formatting
 * - Displays values as formatted currency (e.g., "1,234.56")
 * - Stores internal value as cents (e.g., 123456)
 * - Numbers are inserted from right, moving decimal places
 */
export const useCurrencyInput = (initialCents: number = 0): UseCurrencyInputReturn => {
  const [centValue, setCentValue] = useState<number>(initialCents);

  // Format cents to display string (123456 -> "1,234.56")
  const formatCentsToDisplay = useCallback((cents: number): string => {
    const dollars = cents / 100;
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(dollars);
  }, []);

  // Get current display value
  const displayValue = formatCentsToDisplay(centValue);

  // Handle input changes - numbers insert from right
  const handleChange = useCallback((inputValue: string) => {
    // Remove all non-digit characters
    const digitsOnly = inputValue.replace(/\D/g, '');
    
    // Convert to number (treating as cents)
    const newCents = parseInt(digitsOnly || '0', 10);
    
    // Limit to reasonable max value (999,999,999.99 = 99999999999 cents)
    const maxCents = 99999999999;
    const limitedCents = Math.min(newCents, maxCents);
    
    setCentValue(limitedCents);
  }, []);

  // Set value programmatically (for form initialization/updates)
  const setValue = useCallback((cents: number) => {
    setCentValue(cents);
  }, []);

  return {
    displayValue,
    centValue,
    handleChange,
    setValue,
  };
};